import { useCallback, useEffect, useState } from "react";
import { Send, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, API, formatApiError } from "@/lib/api";
import { mediaUrl } from "@/components/home/ReelBanner";
import { DETAIL_FIELDS } from "@/components/contract/DetailsFields";

const PersonData = ({ title, d }) => !!d && (
  <div data-testid="onboarding-signer-details">
    <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#1c1418]/70">{title}</p>
    <dl className="mt-2 grid grid-cols-1 gap-x-4 gap-y-1 text-sm sm:grid-cols-2">
      {DETAIL_FIELDS.map(([k, label]) => <div key={k}><dt className="inline text-[#1c1418]/60">{label.split(" · ")[1]}: </dt><dd className="inline">{d[k] || "-"}</dd></div>)}
    </dl>
  </div>
);

export const PLANS = [
  ["1", "Link 1 · Youth Management Milano"],
  ["2", "Link 2 · Youthmanagement.com Milano + YouthModels.com Global"],
  ["3", "Link 3 · YouthManagement.com No exclusive"],
];
const STATUS = { invited: "Link inviato, password non ancora creata", active: "In corso", completed: "Tutto firmato", cancelled: "Cancellata dopo 15 giorni" };
const btn = "flex items-center gap-2 bg-[#1c1418] px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] text-white disabled:opacity-60";

const Thumbs = ({ title, items }) => !!items.length && (
  <div>
    <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#1c1418]/70">{title}</p>
    <div className="mt-2 grid grid-cols-4 gap-2 sm:grid-cols-6">
      {items.map((p) => <a key={p.id} href={mediaUrl(p.url)} target="_blank" rel="noopener noreferrer"><img src={mediaUrl(p.url)} alt="" className="aspect-[3/4] w-full object-cover" /></a>)}
    </div>
  </div>
);

const Details = ({ ob, onReactivate, busy }) => (
  <div className="space-y-4" data-testid="onboarding-details">
    <p className="text-sm"><b>{PLANS.find(([k]) => k === ob.plan)?.[1]}</b>{ob.sent_by ? ` · inviato da ${ob.sent_by}` : ""}</p>
    <p data-testid="onboarding-status" className="font-mono text-[11px] uppercase tracking-[0.2em]">
      {STATUS[ob.status]}{["invited", "active"].includes(ob.status) ? ` · ${ob.days_left} giorni rimasti` : ""}
      {ob.reminders.length ? ` · promemoria inviati: giorno ${ob.reminders.join(", ")}` : ""}
    </p>
    {ob.contracts.map((c) => (
      <div key={c.id} className="flex flex-wrap items-center gap-3 text-sm">
        <span>{c.label}</span>
        <span className={`px-2 py-1 font-mono text-[11px] uppercase ${c.status === "signed" ? "bg-[#1c1418] text-white" : "bg-[#f4f4f4]"}`}>{c.status === "signed" ? "Firmato" : "Da firmare"}</span>
        {c.status === "signed" && <a href={`${API}/agent/contracts/${c.id}/pdf?signed=true`} className="font-mono text-xs underline">PDF firmato</a>}
      </div>
    ))}
    <Thumbs title="Snaps" items={ob.snaps} />
    <PersonData title="Dati inseriti dalla modella" d={ob.signer_details} />
    <PersonData title="Dati del genitore" d={ob.parent_details} />
    <Thumbs title="Foto" items={ob.photos} />
    {!!ob.links.length && <div className="text-sm">{ob.links.map((l) => <a key={l} href={l} target="_blank" rel="noopener noreferrer" className="block break-all underline">{l}</a>)}</div>}
    {ob.status === "cancelled" && (
      <button disabled={busy} onClick={onReactivate} data-testid="onboarding-reactivate" className={btn}><RotateCcw className="h-4 w-4 text-[#e9458f]" /> Riattiva processo di firma</button>
    )}
  </div>
);

export const OnboardingBox = ({ appId, scope = "admin" }) => {
  const [ob, setOb] = useState(undefined);
  const [plan, setPlan] = useState("1");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { api.get(`/${scope}/applications/${appId}/onboarding`).then((r) => setOb(r.data?.onboarding ?? null)).catch(() => setOb(null)); }, [appId, scope]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, ok) => {
    setBusy(true);
    try { const { data } = await fn(); setOb(data.onboarding); toast.success(ok); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    setBusy(false);
  };
  if (ob === undefined) return null;
  return (
    <div data-testid="onboarding-box" className="space-y-4 border border-[#1c1418]/20 p-5">
      <p className="font-display text-2xl uppercase">Link modella · contratti</p>
      {ob ? <Details ob={ob} busy={busy} onReactivate={() => act(() => api.post(`/${scope}/onboardings/${ob.id}/reactivate`), "Processo riattivato, email inviata")} /> : (
        <>
          <div className="space-y-2">
            {PLANS.map(([k, l]) => (
              <label key={k} className="flex items-start gap-3 text-sm">
                <input type="radio" name="plan" checked={plan === k} onChange={() => setPlan(k)} data-testid={`onboarding-plan-${k}`} className="mt-1 accent-[#1c1418]" /> {l}
              </label>
            ))}
          </div>
          <button disabled={busy} onClick={() => act(() => api.post(`/${scope}/applications/${appId}/onboarding`, { plan }), "Link inviato alla modella")} data-testid="onboarding-send" className={btn}>
            <Send className="h-4 w-4 text-[#e9458f]" /> {busy ? "Invio…" : "Invia link alla modella"}
          </button>
          <p className="text-xs text-[#1c1418]/70">Riceve un'email per creare la password. Promemoria automatici al giorno 3 e 7; al giorno 15 senza firma la richiesta viene cancellata.</p>
        </>
      )}
    </div>
  );
};
